import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import cn from 'classnames';
import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/mode-json';
import 'ace-builds/src-noconflict/theme-chrome';
import 'ace-builds/src-noconflict/ext-language_tools';

import BaseModal, { BaseModalProps } from '../BaseModal/BaseModal';
import Button from '../Button';
import Input from '../Input';
import Checkbox from '../Checkbox';
import Select from '../Select';

import { useCreateSchemaMutation, useUpdateSchemaMutation } from 'src/api/mutations/schemaMutations';
import { AssetSchema } from 'src/types/TSchemes';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import { Plus, Trash, ArrowUp, ArrowDown, CaretDown, CaretRight } from 'src/common/icons';

import styles from './SchemaEditorModal.module.scss';
import { planUpgradeRequired } from 'src/api/utils/errorHandlers';

// Plan-gate 403s and structured 400s (e.g. the per-plan schema cap) carry an
// actionable message — surface it instead of the generic "Something went
// wrong". Returns null when the error carries nothing structured (call sites
// fall back to getErrorMessage).
function schemaErrorMessage(error: any): string | null {
  const gate = planUpgradeRequired(error);
  if (gate?.message) return gate.message;
  const body = error?.error;
  const errors = body && typeof body === 'object' && (body as any).data?.errors;
  if (Array.isArray(errors) && errors.length) return errors.join(', ');
  return null;
}


// ---------------------------------------------------------------------------
// SchemaField: intermediate representation for the visual editor
// ---------------------------------------------------------------------------

type FieldType = 'string' | 'number' | 'boolean' | 'array';

let _nextFieldId = 1;
function generateFieldId(): string {
  return `field_${_nextFieldId++}_${Date.now()}`;
}

type SchemaField = {
  _id: string; // stable ID for React key — never changes after creation
  key: string;
  title: string;
  type: FieldType;
  description: string;
  format?: string;
  enum?: string[];
  minLength?: number;
  maxLength?: number;
  items?: { title?: string; type?: string; minLength?: number; maxLength?: number };
  displayTitle: string;
  hint: string;
  placeholder: string;
  searchable: boolean;
  keyword_searchable: boolean;
  filterable: boolean;
  uiOrder: number;
  readonly: boolean;
  disabled: boolean;
  hidden: boolean;
  multiline?: boolean;
  isRequired: boolean;
};

// ---------------------------------------------------------------------------
// Conversion helpers
// ---------------------------------------------------------------------------

function schemaToFields(schema: Record<string, any>): SchemaField[] {
  const properties = schema.properties || {};
  const required: string[] = schema.required || [];

  return Object.entries(properties)
    .map(([key, prop]: [string, any]) => ({
      _id: generateFieldId(),
      key,
      title: prop.title || '',
      type: (prop.type || 'string') as FieldType,
      description: prop.description || '',
      format: prop.format,
      enum: prop.enum,
      minLength: prop.minLength,
      maxLength: prop.maxLength,
      items: prop.items,
      displayTitle: prop.filespin_properties?.title?.en || prop.title || '',
      hint: prop.filespin_properties?.hint?.en || '',
      placeholder: prop.filespin_properties?.placeholder?.en || '',
      searchable: prop.filespin_properties?.searchable ?? false,
      keyword_searchable: prop.filespin_properties?.keyword_searchable ?? false,
      filterable: prop.filespin_properties?.filterable ?? false,
      uiOrder: prop.filespin_properties?.ui?.order ?? 999,
      readonly: prop.filespin_properties?.ui?.readonly ?? false,
      disabled: prop.filespin_properties?.ui?.disabled ?? false,
      hidden: prop.filespin_properties?.ui?.hidden ?? false,
      multiline: prop.filespin_properties?.ui?.multiline,
      isRequired: required.includes(key),
    }))
    .sort((a, b) => a.uiOrder - b.uiOrder);
}

function fieldsToSchema(
  fields: SchemaField[],
  originalSchema: Record<string, any>,
): Record<string, any> {
  const properties: Record<string, any> = {};
  const required: string[] = [];

  fields.forEach((field, index) => {
    const prop: Record<string, any> = {
      title: field.title,
      type: field.type,
      description: field.description,
    };

    if (field.format) prop.format = field.format;
    if (field.enum && field.enum.length > 0) prop.enum = field.enum;
    if (field.type === 'string' || field.type === 'array') {
      if (field.minLength !== undefined) prop.minLength = field.minLength;
      if (field.maxLength !== undefined) prop.maxLength = field.maxLength;
    }
    if (field.type === 'array' && field.items) {
      prop.items = field.items;
    }

    const uiSettings: Record<string, any> = {
      order: index + 1,
      readonly: field.readonly,
      disabled: field.disabled,
      hidden: field.hidden,
    };
    if (field.multiline !== undefined) {
      uiSettings.multiline = field.multiline;
    }

    prop.filespin_properties = {
      title: { en: field.displayTitle },
      hint: { en: field.hint },
      placeholder: { en: field.placeholder },
      searchable: field.searchable,
      keyword_searchable: field.keyword_searchable,
      filterable: field.filterable,
      ui: uiSettings,
    };

    properties[field.key] = prop;
    if (field.isRequired) required.push(field.key);
  });

  return {
    ...originalSchema,
    properties,
    required,
  };
}

function createDefaultField(order: number): SchemaField {
  return {
    _id: generateFieldId(),
    key: '',
    title: '',
        type: 'string',
    description: '',
        minLength: 0,
        maxLength: 255,
    displayTitle: '',
    hint: '',
    placeholder: '',
          searchable: true,
    keyword_searchable: false,
          filterable: false,
    uiOrder: order,
            readonly: false,
            disabled: false,
            hidden: false,
    isRequired: false,
  };
}

const DEFAULT_SCHEMA_ENVELOPE: Record<string, any> = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'object',
  title: 'My Schema',
  required: [],
  properties: {},
};

// ---------------------------------------------------------------------------
// Type / format options
// ---------------------------------------------------------------------------

const TYPE_OPTIONS = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'array', label: 'Array (Tags/List)' },
];

const FORMAT_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'date', label: 'Date' },
  { value: 'date-time', label: 'Date-Time' },
  { value: 'email', label: 'Email' },
  { value: 'uri', label: 'URI' },
];

// ---------------------------------------------------------------------------
// FieldCard sub-component
// ---------------------------------------------------------------------------

type FieldCardProps = {
  field: SchemaField;
  index: number;
  isExpanded: boolean;
  isDuplicateKey: boolean;
  isFirst: boolean;
  isLast: boolean;
  onToggle: () => void;
  onChange: (updates: Partial<SchemaField>) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
};

const FieldCard: React.FC<FieldCardProps> = ({
  field,
  index,
  isExpanded,
  isDuplicateKey,
  isFirst,
  isLast,
  onToggle,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}) => {
  const keyInputRef = useRef<HTMLInputElement>(null);
  const didAutoFocusRef = useRef(false);

  // Auto-focus key input once when a new empty field is first expanded
  useEffect(() => {
    if (isExpanded && !didAutoFocusRef.current && keyInputRef.current) {
      keyInputRef.current.focus();
      didAutoFocusRef.current = true;
    }
    if (!isExpanded) {
      didAutoFocusRef.current = false;
    }
  }, [isExpanded]);

  const handleTypeChange = (newType: string) => {
    const updates: Partial<SchemaField> = { type: newType as FieldType };
    // Reset type-specific fields
    if (newType === 'array') {
      updates.items = { title: 'Item', type: 'string', minLength: 1, maxLength: 50 };
      updates.enum = undefined;
      updates.format = undefined;
    } else if (newType === 'number' || newType === 'boolean') {
      updates.minLength = undefined;
      updates.maxLength = undefined;
      updates.enum = undefined;
      updates.format = undefined;
      updates.items = undefined;
      updates.multiline = undefined;
    } else {
      updates.items = undefined;
    }
    onChange(updates);
  };

  const handleEnumChange = (value: string) => {
    const values = value.split('\n').filter((v) => v.trim() !== '');
    onChange({ enum: values.length > 0 ? values : undefined });
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm(`Remove field "${field.key || '(unnamed)'}"?`)) {
      onRemove();
    }
  };

  return (
    <div className={cn(styles.fieldCard, { [styles.fieldCardExpanded]: isExpanded, [styles.fieldCardError]: isDuplicateKey })}>
      {/* Collapsed header — always visible */}
      <div className={styles.fieldHeader} onClick={onToggle}>
        <span className={styles.fieldExpandIcon}>
          {isExpanded ? <CaretDown /> : <CaretRight />}
        </span>
        <span className={styles.fieldIndex}>{index + 1}</span>
        <code className={cn(styles.fieldKey, { [styles.fieldKeyError]: isDuplicateKey || (!field.key && isExpanded) })}>
          {field.key || '(no key)'}
        </code>
        <span className={styles.fieldDisplayTitle}>
          {field.displayTitle || field.title || ''}
        </span>
        <span className={styles.typeBadge}>{field.type}</span>
        {field.enum && field.enum.length > 0 && (
          <span className={styles.enumBadge}>enum</span>
        )}
        {field.format && (
          <span className={styles.formatBadge}>{field.format}</span>
        )}
        <div className={styles.fieldHeaderActions}>
          <button
            className={styles.fieldActionBtn}
            onClick={(e) => { e.stopPropagation(); onMoveUp(); }}
            disabled={isFirst}
            title="Move up"
          >
            <ArrowUp />
          </button>
          <button
            className={styles.fieldActionBtn}
            onClick={(e) => { e.stopPropagation(); onMoveDown(); }}
            disabled={isLast}
            title="Move down"
          >
            <ArrowDown />
          </button>
          <button
            className={cn(styles.fieldActionBtn, styles.fieldActionBtnDelete)}
            onClick={handleRemoveClick}
            title="Remove field"
          >
            <Trash />
          </button>
        </div>
      </div>

      {/* Expanded form */}
      {isExpanded && (
        <div className={styles.fieldBody}>
          {/* Basic info */}
          <div className={styles.fieldSection}>
            <div className={styles.fieldRow}>
              <div className={styles.fieldCol}>
                <label className={styles.fieldFormLabel}>Field Key</label>
                <input
                  ref={keyInputRef}
                  className={cn(styles.fieldInput, { [styles.fieldInputError]: isDuplicateKey || !field.key })}
                  value={field.key}
                  onChange={(e) => onChange({ key: e.target.value.replace(/\s/g, '_').toLowerCase() })}
                  placeholder="e.g. event_name"
                />
                {isDuplicateKey && (
                  <span className={styles.fieldErrorHint}>Duplicate key</span>
                )}
              </div>
              <div className={styles.fieldCol}>
                <label className={styles.fieldFormLabel}>Type</label>
                <Select
                  value={field.type}
                  onChange={handleTypeChange}
                  options={TYPE_OPTIONS}
                  className={styles.fieldSelect}
                />
              </div>
            </div>
            <div className={styles.fieldRow}>
              <div className={styles.fieldCol}>
                <label className={styles.fieldFormLabel}>Display Title</label>
                <input
                  className={styles.fieldInput}
                  value={field.displayTitle}
                  onChange={(e) => onChange({ displayTitle: e.target.value, title: e.target.value })}
                  placeholder="e.g. Event Name"
                />
              </div>
              <div className={styles.fieldCol}>
                <label className={styles.fieldFormLabel}>Description</label>
                <input
                  className={styles.fieldInput}
                  value={field.description}
                  onChange={(e) => onChange({ description: e.target.value })}
                  placeholder="What this field is for"
                />
              </div>
            </div>
          </div>

          {/* Constraints — string type */}
          {field.type === 'string' && (
            <div className={styles.fieldSection}>
              <label className={styles.fieldSectionTitle}>Constraints</label>
              <div className={styles.fieldRow}>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldFormLabel}>Format</label>
                  <Select
                    value={field.format || ''}
                    onChange={(v) => onChange({ format: v || undefined })}
                    options={FORMAT_OPTIONS}
                    className={styles.fieldSelect}
                  />
                </div>
                <div className={styles.fieldColHalf}>
                  <label className={styles.fieldFormLabel}>Min Length</label>
                  <input
                    className={styles.fieldInput}
                    type="number"
                    min={0}
                    value={field.minLength ?? 0}
                    onChange={(e) => onChange({ minLength: Number(e.target.value) })}
                  />
                </div>
                <div className={styles.fieldColHalf}>
                  <label className={styles.fieldFormLabel}>Max Length</label>
                  <input
                    className={styles.fieldInput}
                    type="number"
                    min={0}
                    value={field.maxLength ?? 255}
                    onChange={(e) => onChange({ maxLength: Number(e.target.value) })}
                  />
                </div>
              </div>
              <div className={styles.fieldRow}>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldFormLabel}>
                    Enum Options
                    <span className={styles.fieldFormHint}> (one per line, leave empty for free text)</span>
                  </label>
                  <textarea
                    className={styles.enumTextarea}
                    value={(field.enum || []).join('\n')}
                    onChange={(e) => handleEnumChange(e.target.value)}
                    placeholder={'draft\nin_review\napproved\npublished'}
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Array config */}
          {field.type === 'array' && (
            <div className={styles.fieldSection}>
              <label className={styles.fieldSectionTitle}>Array Item Settings</label>
              <div className={styles.fieldRow}>
                <div className={styles.fieldCol}>
                  <label className={styles.fieldFormLabel}>Item Title</label>
                  <input
                    className={styles.fieldInput}
                    value={field.items?.title || ''}
                    onChange={(e) =>
                      onChange({ items: { ...field.items, title: e.target.value, type: field.items?.type || 'string' } })
                    }
                    placeholder="e.g. Tag"
                  />
                </div>
                <div className={styles.fieldColHalf}>
                  <label className={styles.fieldFormLabel}>Item Min Length</label>
                  <input
                    className={styles.fieldInput}
                    type="number"
                    min={0}
                    value={field.items?.minLength ?? 1}
                    onChange={(e) =>
                      onChange({ items: { ...field.items, type: field.items?.type || 'string', minLength: Number(e.target.value) } })
                    }
                  />
                </div>
                <div className={styles.fieldColHalf}>
                  <label className={styles.fieldFormLabel}>Item Max Length</label>
                  <input
                    className={styles.fieldInput}
                    type="number"
                    min={0}
                    value={field.items?.maxLength ?? 50}
                    onChange={(e) =>
                      onChange({ items: { ...field.items, type: field.items?.type || 'string', maxLength: Number(e.target.value) } })
                    }
                  />
                </div>
              </div>
            </div>
          )}

          {/* FileSpin properties */}
          <div className={styles.fieldSection}>
            <label className={styles.fieldSectionTitle}>Display Settings</label>
            <div className={styles.fieldRow}>
              <div className={styles.fieldCol}>
                <label className={styles.fieldFormLabel}>Hint Text</label>
                <input
                  className={styles.fieldInput}
                  value={field.hint}
                  onChange={(e) => onChange({ hint: e.target.value })}
                  placeholder="Help text shown to users"
                />
              </div>
              <div className={styles.fieldCol}>
                <label className={styles.fieldFormLabel}>Placeholder</label>
                <input
                  className={styles.fieldInput}
                  value={field.placeholder}
                  onChange={(e) => onChange({ placeholder: e.target.value })}
                  placeholder="Input placeholder text"
                />
              </div>
            </div>
          </div>

          {/* Search / filter toggles */}
          <div className={styles.fieldSection}>
            <label className={styles.fieldSectionTitle}>Search &amp; Filter</label>
            <div className={styles.toggleRow}>
              <label className={styles.toggleItem}>
                <Checkbox
                  checked={field.searchable}
                  onChange={(e) => onChange({ searchable: e.target.checked })}
                />
                <span>Searchable</span>
              </label>
              <label className={styles.toggleItem}>
                <Checkbox
                  checked={field.keyword_searchable}
                  onChange={(e) => onChange({ keyword_searchable: e.target.checked })}
                />
                <span>Keyword Searchable</span>
              </label>
              <label className={styles.toggleItem}>
                <Checkbox
                  checked={field.isRequired}
                  onChange={(e) => onChange({ isRequired: e.target.checked })}
                />
                <span>Required</span>
              </label>
            </div>
          </div>

          {/* UI toggles */}
          <div className={styles.fieldSection}>
            <label className={styles.fieldSectionTitle}>UI Behavior</label>
            <div className={styles.toggleRow}>
              <label className={styles.toggleItem}>
                <Checkbox
                  checked={field.readonly}
                  onChange={(e) => onChange({ readonly: e.target.checked })}
                />
                <span>Read-only</span>
              </label>
              <label className={styles.toggleItem}>
                <Checkbox
                  checked={field.disabled}
                  onChange={(e) => onChange({ disabled: e.target.checked })}
                />
                <span>Disabled</span>
              </label>
              <label className={styles.toggleItem}>
                <Checkbox
                  checked={field.hidden}
                  onChange={(e) => onChange({ hidden: e.target.checked })}
                />
                <span>Hidden</span>
              </label>
              {field.type === 'string' && (
                <label className={styles.toggleItem}>
                  <Checkbox
                    checked={field.multiline ?? false}
                    onChange={(e) => onChange({ multiline: e.target.checked })}
                  />
                  <span>Multiline</span>
                </label>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SchemaEditorModal
// ---------------------------------------------------------------------------

type SchemaEditorModalProps = BaseModalProps & {
  schema?: AssetSchema; // If provided, we're editing; otherwise creating
  onSuccess?: () => void;
};

export const SchemaEditorModal: React.FC<SchemaEditorModalProps> = ({
  schema,
  onSuccess,
  ...modalProps
}) => {
  const { closeModal } = useModal();
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();

  const isEditing = !!schema;

  // Schema name
  const [name, setName] = useState(schema?.name?.en || '');

  // Tab state
  const [activeTab, setActiveTab] = useState<'visual' | 'json'>('visual');

  // Visual editor state
  const [fields, setFields] = useState<SchemaField[]>(
    schema ? schemaToFields(schema.schema) : [createDefaultField(1)],
  );
  const [expandedFieldIndex, setExpandedFieldIndex] = useState<number | null>(
    schema ? null : 0,
  );

  // JSON editor state
  const [schemaBody, setSchemaBody] = useState(
    schema
      ? JSON.stringify(schema.schema, null, 2)
      : JSON.stringify(DEFAULT_SCHEMA_ENVELOPE, null, 2),
  );
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Keep a reference to the original schema envelope for round-tripping
  const originalSchemaRef = useRef<Record<string, any>>(
    schema?.schema ? { ...schema.schema } : { ...DEFAULT_SCHEMA_ENVELOPE },
  );

  // -------------------------------------------------------------------------
  // Tab switching with bidirectional sync
  // -------------------------------------------------------------------------

  const handleTabSwitch = useCallback(
    (tab: 'visual' | 'json') => {
      if (tab === activeTab) return;

      if (tab === 'json') {
        // Visual → JSON: serialize current fields
        const fullSchema = fieldsToSchema(fields, originalSchemaRef.current);
        const json = JSON.stringify(fullSchema, null, 2);
        setSchemaBody(json);
        setJsonError(null);
      } else {
        // JSON → Visual: parse and convert
        try {
          const parsed = JSON.parse(schemaBody);
          setFields(schemaToFields(parsed));
          originalSchemaRef.current = { ...parsed };
          setJsonError(null);
        } catch (e: any) {
          showAlert(`Cannot switch to visual editor: invalid JSON. ${e.message}`);
          return; // Stay on JSON tab
        }
      }
      setActiveTab(tab);
    },
    [activeTab, fields, schemaBody, showAlert],
  );

  // -------------------------------------------------------------------------
  // JSON editor handlers
  // -------------------------------------------------------------------------

  const handleEditorChange = (value: string) => {
    setSchemaBody(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (e: any) {
      setJsonError(e.message);
    }
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(schemaBody);
      setSchemaBody(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (e: any) {
      setJsonError(e.message);
    }
  };

  // -------------------------------------------------------------------------
  // Field CRUD operations
  // -------------------------------------------------------------------------

  const updateField = useCallback((index: number, updates: Partial<SchemaField>) => {
    setFields((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, []);

  const removeField = useCallback((index: number) => {
    setFields((prev) => prev.filter((_, i) => i !== index));
    setExpandedFieldIndex((prev) => {
      if (prev === index) return null;
      if (prev !== null && prev > index) return prev - 1;
      return prev;
    });
  }, []);

  const moveField = useCallback((fromIndex: number, toIndex: number) => {
    if (toIndex < 0) return;
    setFields((prev) => {
      if (toIndex >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
    setExpandedFieldIndex((prev) => {
      if (prev === fromIndex) return toIndex;
      if (prev === toIndex) return fromIndex;
      return prev;
    });
  }, []);

  const addField = useCallback(() => {
    const newField = createDefaultField(fields.length + 1);
    setFields((prev) => [...prev, newField]);
    setExpandedFieldIndex(fields.length);
  }, [fields.length]);

  // -------------------------------------------------------------------------
  // Validation
  // -------------------------------------------------------------------------

  const duplicateKeys = useMemo(() => {
    const keyCounts: Record<string, number> = {};
    fields.forEach((f) => {
      if (f.key) {
        keyCounts[f.key] = (keyCounts[f.key] || 0) + 1;
      }
    });
    const dupes = new Set<string>();
    Object.entries(keyCounts).forEach(([key, count]) => {
      if (count > 1) dupes.add(key);
    });
    return dupes;
  }, [fields]);

  const hasEmptyKeys = useMemo(() => fields.some((f) => !f.key.trim()), [fields]);

  const isVisualValid = useMemo(() => {
    return (
      name.trim().length > 0 &&
      fields.length > 0 &&
      !hasEmptyKeys &&
      duplicateKeys.size === 0
    );
  }, [name, fields, hasEmptyKeys, duplicateKeys]);

  const isJsonValid = useMemo(() => {
    return name.trim().length > 0 && !jsonError;
  }, [name, jsonError]);

  const isValid = activeTab === 'visual' ? isVisualValid : isJsonValid;

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const createMutation = useCreateSchemaMutation({
    onSuccess: () => {
      showSuccess('Schema created successfully');
      closeModal();
      onSuccess?.();
    },
    onError: (error: any) => {
      showAlert(schemaErrorMessage(error) ?? getErrorMessage(error.message));
    },
  });

  const updateMutation = useUpdateSchemaMutation(schema?.id || 0, {
    onSuccess: () => {
      showSuccess('Schema updated successfully');
      closeModal();
      onSuccess?.();
    },
    onError: (error: any) => {
      showAlert(schemaErrorMessage(error) ?? getErrorMessage(error.message));
    },
  });

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------

  const handleSubmit = () => {
    if (!name.trim()) {
      showAlert('Schema name is required');
      return;
    }

    let parsedSchema: Record<string, any>;

    if (activeTab === 'visual') {
      // Validate visual fields
      if (hasEmptyKeys) {
        showAlert('All fields must have a key');
        return;
      }
      if (duplicateKeys.size > 0) {
        showAlert(`Duplicate field keys: ${Array.from(duplicateKeys).join(', ')}`);
        return;
      }
      parsedSchema = fieldsToSchema(fields, originalSchemaRef.current);
    } else {
    try {
      parsedSchema = JSON.parse(schemaBody);
    } catch (e: any) {
      showAlert(`Invalid JSON: ${e.message}`);
      return;
      }
    }

    if (isEditing) {
      updateMutation.mutate({
        name: { en: name.trim() },
        status: schema!.status || 'ACTIVE',
        schema: parsedSchema,
      });
    } else {
      createMutation.mutate({
        name: { en: name.trim() },
        status: 'ACTIVE',
        schema: parsedSchema,
      });
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <BaseModal {...modalProps} containerStyle={{ maxWidth: '960px', width: '92vw' }}>
      <h2 className={styles.modalTitle}>
        {isEditing ? 'Edit Schema' : 'Create Custom Schema'}
      </h2>
      <p className={styles.modalDescription}>
        {isEditing
          ? 'Modify the schema fields using the visual editor, or switch to Raw JSON for advanced editing.'
          : 'Define a new asset schema by adding fields. Each field becomes a metadata property on your assets.'}
      </p>

      {/* Schema name */}
      <div className={styles.formSection}>
        <label className={styles.fieldLabel}>Schema Name</label>
        <Input
          variant="secondary"
          value={name}
          onChange={(value: string) => setName(value)}
          placeholder="e.g. Marketing Media Asset Schema"
        />
      </div>

      {/* Tab switcher */}
      <div className={styles.tabSwitcher}>
        <button
          className={cn(styles.tab, { [styles.tabActive]: activeTab === 'visual' })}
          onClick={() => handleTabSwitch('visual')}
        >
          Fields Editor
          {activeTab === 'visual' && fields.length > 0 && (
            <span className={styles.tabCount}>{fields.length}</span>
          )}
        </button>
        <button
          className={cn(styles.tab, { [styles.tabActive]: activeTab === 'json' })}
          onClick={() => handleTabSwitch('json')}
        >
          Raw JSON
        </button>
      </div>

      {/* Visual editor tab */}
      {activeTab === 'visual' && (
        <div className={styles.visualEditor}>
          <div className={styles.fieldsList}>
            {fields.length === 0 && (
              <div className={styles.emptyFields}>
                <p>No fields yet. Click "Add Field" to get started.</p>
              </div>
            )}
            {fields.map((field, index) => (
              <FieldCard
                key={field._id}
                field={field}
                index={index}
                isExpanded={expandedFieldIndex === index}
                isDuplicateKey={duplicateKeys.has(field.key)}
                isFirst={index === 0}
                isLast={index === fields.length - 1}
                onToggle={() =>
                  setExpandedFieldIndex((prev) => (prev === index ? null : index))
                }
                onChange={(updates) => updateField(index, updates)}
                onRemove={() => removeField(index)}
                onMoveUp={() => moveField(index, index - 1)}
                onMoveDown={() => moveField(index, index + 1)}
              />
            ))}
          </div>
          <button className={styles.addFieldButton} onClick={addField}>
            <Plus />
            <span>Add Field</span>
          </button>
        </div>
      )}

      {/* JSON editor tab */}
      {activeTab === 'json' && (
        <div className={styles.jsonEditor}>
        <div className={styles.editorHeader}>
            <span className={styles.editorHint}>
              Edit the full JSON Schema directly. Changes sync back to the visual editor.
            </span>
          <button type="button" className={styles.formatButton} onClick={handleFormat}>
            Format JSON
          </button>
        </div>
        <div className={styles.editorWrapper}>
          <AceEditor
            mode="json"
            theme="chrome"
            onChange={handleEditorChange}
            value={schemaBody}
            fontSize={13}
            width="100%"
            height="400px"
            editorProps={{ $blockScrolling: true }}
            setOptions={{
              showLineNumbers: true,
              tabSize: 2,
              useWorker: false,
            }}
          />
        </div>
        {jsonError && (
            <div className={styles.jsonError}>JSON Error: {jsonError}</div>
          )}
          </div>
        )}

      {/* Actions */}
      <div className={styles.actions}>
        <Button mode="tertiary" size="small" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          mode="primary"
          size="small"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!isValid || isLoading}
        >
          {isEditing ? 'Save Changes' : 'Create Schema'}
        </Button>
      </div>
    </BaseModal>
  );
};

export default SchemaEditorModal;
